import React, { useState, useEffect, useMemo, Suspense } from 'react';
import {
  Product,
  productService,
  ProductAsset,
  ProductPrice,
  QUERY_KEYS,
} from '@/services/productService';
import { IncompleteProduct, getIncompleteProducts } from '@/services/dashboardService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  ImageIcon,
  AlertTriangle,
  PlusIcon,
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  List,
  Loader2,
  Printer,
} from 'lucide-react';
import ProductDetailDescription from '@/components/products/ProductDetailDescription';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { cn } from '@/lib/utils';
import { CompletenessDrilldown } from './CompletenessDrilldown';
import RelatedProductsPanel from './RelatedProductsPanel';
import { AssetsTab } from './AssetsTab';
import { Skeleton } from '@/components/ui/skeleton';
import ProductHistoryTab from './ProductHistoryTab';
import { ProductAttributesPanel } from '@/components/ProductAttributesPanel';
import { ENABLE_CUSTOM_ATTRIBUTES } from '@/config/featureFlags';
import { Badge } from '@/components/ui/badge';
import { FieldStatusModal } from './FieldStatusModal';
import { getAssetUrl } from '@/utils/isImageAsset';
import { getImageUrlFromAsset } from '@/utils/images';
import PriceTab from './PriceTab';
import { LocaleCode } from '@/services/types';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { config, API_ENDPOINTS, API_CURRENCIES } from '@/config/config';
import { useEffectiveAttributes } from '@/hooks/useEffectiveAttributes';
import { ProductCommentsTab } from '@/components/product/ProductCommentsTab';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { logError, logDebug, logInfo, logWarn } from '@/utils/errorHandler';

interface ProductDetailTabsProps {
  product: Product;
  prices: ProductPrice[];
  isPricesLoading: boolean;
  onProductUpdate: (updatedProduct: Product) => Promise<void>;
}

export const ProductDetailTabs = ({
  product,
  prices,
  isPricesLoading,
  onProductUpdate,
}: ProductDetailTabsProps): JSX.Element => {
  // i18n Step 4a: Get translations hook
  const { getTabsLabels, getTabsNotification } = useProductDetailPageTranslations();

  // Get reference to productDetailTabs config section
  const detailTabsConfig = config.productDetailTabs;

  const [activeTab, setActiveTab] = useState('overview');

  // States for dynamic data
  const [assets, setAssets] = useState<ProductAsset[]>([]);

  // Loading states
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Get query client for cache monitoring
  const queryClient = useQueryClient();

  // Add debugging effect for attributes tab
  useEffect(() => {
    // Effect intentionally empty - placeholder for future debugging needs
  }, [activeTab, product.id]);

  const { defaultLocale, defaultChannel } = useOrgSettings();
  const [selectedLocale, setSelectedLocale] = useState<LocaleCode>('en_US'); // Initialize with stable default

  // Update selectedLocale when defaultLocale becomes available
  useEffect(() => {
    if (defaultLocale && selectedLocale === 'en_US') {
      setSelectedLocale(defaultLocale);
    }
  }, [defaultLocale, selectedLocale]);

  // Data completeness state
  const [showDrilldown, setShowDrilldown] = useState(false);

  // New state for completeness data
  const [productCompletenessDetails, setProductCompletenessDetails] =
    useState<IncompleteProduct | null>(null);
  const [isLoadingCompleteness, setIsLoadingCompleteness] = useState<boolean>(false);

  // Get current user and permissions from auth context
  const { user, checkPermission } = useAuth();

  // Permissions check
  const hasViewPermission = checkPermission('product.view');
  const hasEditPermission = checkPermission('product.change');
  const hasAddPermission = checkPermission('product.add');

  // Add effective attributes data for comparison
  const { data: effectiveAttributesData } = useEffectiveAttributes(
    product.id ?? 0,
    defaultLocale,
    undefined, // channel
    Boolean(product.id)
  );

  // Calculate filtered missing fields (moved outside conditional rendering)
  const filteredMissingFields = useMemo(() => {
    const missingFields: any[] = [];

    // STEP 1: Check all core fields proactively
    const coreFieldChecks = [
      {
        field: getTabsLabels.dataCompleteness.fieldNames.name,
        weight: 2,
        hasValue: Boolean(product.name && product.name.trim()),
        value: product.name,
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.sku,
        weight: 2,
        hasValue: Boolean(product.sku && product.sku.trim()),
        value: product.sku,
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.description,
        weight: 1.5,
        hasValue: Boolean(product.description && product.description.trim()),
        value: product.description,
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.category,
        weight: 1.5,
        hasValue: Boolean(
          (Array.isArray(product.category) && product.category.length > 0) ||
            (typeof product.category === 'object' &&
              product.category !== null &&
              'id' in product.category) ||
            (typeof product.category === 'string' && product.category.trim() !== '')
        ),
        value: product.category,
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.brand,
        weight: 1,
        hasValue: Boolean(product.brand && product.brand.trim()),
        value: product.brand,
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.gtinBarcode,
        weight: 1,
        hasValue: Boolean(product.barcode && product.barcode.trim()),
        value: product.barcode,
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.tags,
        weight: 1,
        hasValue: Boolean(product.tags && Array.isArray(product.tags) && product.tags.length > 0),
        value: product.tags,
      },
    ];

    // Add missing core fields
    coreFieldChecks.forEach(check => {
      if (!check.hasValue) {
        missingFields.push({
          field: check.field,
          weight: check.weight,
          is_core_field: true,
        });
      }
    });

    // STEP 2: Check images
    const hasRealImages = assets.some(asset => {
      const name = (asset.name || '').toLowerCase();
      const url = (asset.url || '').toLowerCase();
      const type = (asset.type || '').toLowerCase();

      const isImageByExt =
        /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name) || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
      const isImageByType = type.includes('image/') && !type.includes('pdf');

      return isImageByExt || isImageByType;
    });

    if (!hasRealImages) {
      missingFields.push({
        field: getTabsLabels.dataCompleteness.fieldNames.images,
        weight: 1.5,
        is_core_field: true,
      });
    }

    // STEP 3: Add missing attributes from effective attributes data
    if (effectiveAttributesData) {
      effectiveAttributesData.forEach(group => {
        group.attributes.forEach(attr => {
          const isRequired = group.required || attr.is_required;
          if (isRequired && attr.has_record === false) {
            missingFields.push({
              field: `Attribute: ${attr.label}`,
              weight: 1,
              attribute_id: attr.id,
              attribute_code: attr.code,
              attribute_type: attr.data_type,
              attr_required: attr.is_required,
              group_required: group.required,
            });
          }
        });
      });
    } else {
      // STEP 4: Fallback - use backend missing fields for attributes only
      if (productCompletenessDetails?.missing_fields) {
        const backendAttributeFields = productCompletenessDetails.missing_fields.filter(item => {
          // Handle case where item might be a string or object
          if (typeof item === 'string') {
            return item.toLowerCase().includes('attribute:');
          }

          // Handle case where item is an object with properties
          if (typeof item === 'object' && item !== null) {
            const isAttributeField =
              ('attribute_id' in item && (item as any).attribute_id) ||
              ('attribute_code' in item && (item as any).attribute_code) ||
              ('field' in item &&
                typeof (item as any).field === 'string' &&
                (item as any).field.toLowerCase().includes('attribute:'));
            return isAttributeField;
          }

          return false;
        });

        backendAttributeFields.forEach(item => {
          missingFields.push(item);
        });
      }
    }

    return missingFields;
  }, [product, assets, effectiveAttributesData, productCompletenessDetails]);

  // Categorize missing fields by priority based on field type (core vs attribute)
  const categorizedMissingFields = useMemo(() => {
    if (!filteredMissingFields.length) return { critical: [], recommended: [], optional: [] };

    // Critical fields explicitly include core fields and specific attributes like GTIN and Category
    const critical = filteredMissingFields.filter(item => {
      // 1) Core fields (no attribute identifiers)
      const isCore = !item.attribute_id && !item.attribute_code;

      // 2) Required attribute groups
      const isRequiredAttribute = item.group_required === true || item.attr_required === true;

      // 3) Specific high-priority keywords (GTIN, category, etc.)
      const hasPriorityKeyword = ['gtin', 'barcode', 'upc', 'ean', 'category', 'categories'].some(
        term => item.field.toLowerCase().includes(term)
      );

      return isCore || isRequiredAttribute || hasPriorityKeyword;
    });

    // Other fields with attribute_id or attribute_code that aren't already in critical
    const attributeFields = filteredMissingFields.filter(
      item =>
        (item.attribute_id || item.attribute_code) &&
        !critical.some(criticalItem => criticalItem.field === item.field)
    );

    // Further categorize attributes by weight
    const recommended = attributeFields.filter(item => item.weight >= 50);
    const optional = attributeFields.filter(item => item.weight < 50);

    return { critical, recommended, optional };
  }, [filteredMissingFields]);

  // Calculate field completeness based on effective attributes data (new logic)
  const fieldCompletenessFromEffectiveData = useMemo(() => {
    if (!effectiveAttributesData) return [];

    const fieldCompleteness = [];

    // Check if product has real images (not just placeholder or PDF files)
    const hasRealImages = assets.some(asset => {
      const name = (asset.name || '').toLowerCase();
      const url = (asset.url || '').toLowerCase();
      const type = (asset.type || '').toLowerCase();

      const isImageByExt =
        /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name) || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
      const isImageByType = type.includes('image/') && !type.includes('pdf');

      return isImageByExt || isImageByType;
    });

    // Add core fields to the field completeness
    const coreFields = [
      {
        field: getTabsLabels.dataCompleteness.fieldNames.name,
        weight: 2,
        complete: Boolean(product.name && product.name.trim()),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.sku,
        weight: 2,
        complete: Boolean(product.sku && product.sku.trim()),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.description,
        weight: 1.5,
        complete: Boolean(product.description && product.description.trim()),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.category,
        weight: 1.5,
        complete: Boolean(
          (Array.isArray(product.category) && product.category.length > 0) ||
            (typeof product.category === 'object' &&
              product.category !== null &&
              'id' in product.category) ||
            (typeof product.category === 'string' && product.category.trim() !== '')
        ),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.brand,
        weight: 1,
        complete: Boolean(product.brand && product.brand.trim()),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.gtinBarcode,
        weight: 1,
        complete: Boolean(product.barcode && product.barcode.trim()),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.tags,
        weight: 1,
        complete: Boolean(product.tags && Array.isArray(product.tags) && product.tags.length > 0),
      },
      {
        field: getTabsLabels.dataCompleteness.fieldNames.images,
        weight: 1.5,
        complete: hasRealImages,
      },
    ];

    fieldCompleteness.push(...coreFields);

    // Add attribute fields ONLY from required attribute groups
    effectiveAttributesData.forEach(group => {
      group.attributes.forEach(attr => {
        const isRequired = group.required || attr.is_required;
        if (isRequired) {
          fieldCompleteness.push({
            field: `Attribute: ${attr.label}`,
            weight: 1,
            complete: attr.has_record === true,
            attribute_id: attr.id,
            attribute_code: attr.code,
            attribute_type: attr.data_type,
            attr_required: attr.is_required,
            group_required: group.required,
          });
        }
      });
    });

    return fieldCompleteness;
  }, [effectiveAttributesData, product, assets]);

  // Use the new field completeness data or fallback to old data
  const currentFieldCompleteness = effectiveAttributesData
    ? fieldCompletenessFromEffectiveData
    : productCompletenessDetails?.field_completeness || [];

  // Calculate completeness percentage based on effective attributes data
  const calculatedCompletenessPercentage = useMemo(() => {
    if (effectiveAttributesData) {
      // Attribute-level requiredness
      let totalAttributes = 0;
      let filledAttributes = 0;
      effectiveAttributesData.forEach(group => {
        group.attributes.forEach(attr => {
          if (group.required || attr.is_required) {
            totalAttributes += 1;
            if (attr.has_record === true) filledAttributes += 1;
          }
        });
      });

      // Check if product has real images (same logic as above)
      const hasRealImages = assets.some(asset => {
        const name = (asset.name || '').toLowerCase();
        const url = (asset.url || '').toLowerCase();
        const type = (asset.type || '').toLowerCase();

        const isImageByExt =
          /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name) ||
          /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
        const isImageByType = type.includes('image/') && !type.includes('pdf');

        return isImageByExt || isImageByType;
      });

      // Add core fields to the calculation
      const coreFields = ['name', 'sku', 'category', 'brand', 'tags', 'barcode', 'images'];
      const filledCoreFields = coreFields.filter(field => {
        switch (field) {
          case 'name':
            return product.name && product.name.trim() !== '';
          case 'sku':
            return product.sku && product.sku.trim() !== '';
          case 'category':
            return (
              (Array.isArray(product.category) && product.category.length > 0) ||
              (typeof product.category === 'object' &&
                product.category !== null &&
                'id' in product.category) ||
              (typeof product.category === 'string' && product.category.trim() !== '')
            );
          case 'brand':
            return product.brand && product.brand.trim() !== '';
          case 'tags':
            return product.tags && Array.isArray(product.tags) && product.tags.length > 0;
          case 'barcode':
            return product.barcode && product.barcode.trim() !== '';
          case 'images':
            return hasRealImages;
          default:
            return false;
        }
      }).length;

      const totalFields = totalAttributes + coreFields.length;
      const filledFields = filledAttributes + filledCoreFields;

      const percentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 100;

      return percentage;
    }

    // Fallback to backend data if effective attributes not available
    return productCompletenessDetails?.completeness || 0;
  }, [effectiveAttributesData, product, productCompletenessDetails?.completeness, assets]);

  // Load data when component mounts or product changes
  useEffect(() => {
    if (product.id) {
      // Always fetch assets on initial load, regardless of activeTab
      fetchAssets();
    }
  }, [product.id]); // Only depend on product.id, not activeTab

  // Refresh assets when product is updated (e.g., after image upload)
  useEffect(() => {
    if (product.id && product.updated_at) {
      // Fetch assets when the product's updated_at timestamp changes
      // This will catch updates from ProductForm and ProductDetailSidebar
      fetchAssets();
    }
  }, [product.updated_at, product.id]);

  // Force refresh assets when navigating from product creation
  useEffect(() => {
    // Check if we're coming from a product creation flow
    const urlParams = new URLSearchParams(window.location.search);
    const isNewProduct = urlParams.get('new') === 'true';

    if (isNewProduct && product.id) {
      // Clear cache and force fresh fetch for new products
      localStorage.removeItem(`product_assets_${product.id}`);
      fetchAssets();

      // Clean up URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [product.id]);

  // Listen for asset cache invalidation events
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If the asset cache for this product was cleared, refresh assets
      if (e.key === `product_assets_${product.id}` && e.newValue === null) {
        fetchAssets();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [product.id]);

  // Listen for React Query cache changes for assets
  useEffect(() => {
    if (!product.id) return;

    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      // Check if this is an asset-related query update for our product
      if (event.type === 'updated' && event.query.queryKey) {
        const queryKey = event.query.queryKey;

        // Check if it's a product assets query for our product (using correct query key)
        if (
          Array.isArray(queryKey) &&
          queryKey.length >= 2 &&
          queryKey[0] === 'assets' &&
          queryKey[1] === product.id
        ) {
          fetchAssets();
        }

        // Also check for general product query updates that might include asset changes
        if (
          Array.isArray(queryKey) &&
          queryKey.length >= 2 &&
          queryKey[0] === 'product' &&
          queryKey[1] === product.id
        ) {
          fetchAssets();
        }
      }
    });

    return unsubscribe;
  }, [product.id, queryClient]);

  // Listen for custom asset update events
  useEffect(() => {
    const handleAssetUpdate = (event: CustomEvent) => {
      if (event.detail?.productId === product.id) {
        fetchAssets();
      }
    };

    window.addEventListener('productAssetsUpdated', handleAssetUpdate as EventListener);
    return () => {
      window.removeEventListener('productAssetsUpdated', handleAssetUpdate as EventListener);
    };
  }, [product.id]);

  // This effect refreshes assets data when switching to the assets tab
  useEffect(() => {
    // Refresh assets when switching to assets tab
    if (activeTab === 'assets' && product.id) {
      // Remove non-essential console log
      fetchAssets();

      // Set up a polling interval to check for asset changes while on this tab
      const refreshInterval = setInterval(() => {
        fetchAssets();
      }, 10000); // Check every 10 seconds

      // Clean up the interval when changing tabs
      return () => clearInterval(refreshInterval);
    }
  }, [activeTab, product.id]);

  // Fetch assets from API
  const fetchAssets = async () => {
    if (!product?.id) return;

    setLoadingAssets(true);

    // First, try to get fresh data from React Query cache
    try {
      const cachedAssets = queryClient.getQueryData(QUERY_KEYS.PRODUCT_ASSETS(product.id));
      if (Array.isArray(cachedAssets) && cachedAssets.length > 0) {
        setAssets(cachedAssets);
        setLoadingAssets(false);
        return;
      }
    } catch (err) {
      // Continue to API fetch if cache fails
    }

    // Try to fetch from API
    let fetchedAssets: ProductAsset[] = [];
    try {
      // Attempt to get assets from API
      fetchedAssets = await productService.getProductAssets(product.id);

      // If we got a valid response with at least one asset
      if (Array.isArray(fetchedAssets) && fetchedAssets.length > 0) {
        // Normalize asset data structure to ensure consistency
        const normalizedAssets = fetchedAssets.map(asset => ({
          ...asset,
          // Ensure type is set for proper detection
          type:
            asset.type ||
            asset.asset_type ||
            (/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(asset.url?.toLowerCase() || '')
              ? 'image'
              : 'unknown'),
          // Ensure basic properties exist
          name: asset.name || `Asset ${asset.id}`,
          size: asset.size || '0',
          uploaded_at: asset.uploaded_at || new Date().toISOString(),
          uploaded_by: asset.uploaded_by || 'system',
        }));

        setAssets(normalizedAssets);

        // Cache the assets in localStorage
        localStorage.setItem(`product_assets_${product.id}`, JSON.stringify(normalizedAssets));
        setLoadingAssets(false);
        return;
      }
    } catch (err) {
      logError(
        'Failed to fetch product assets from API',
        {
          component: 'ProductDetailTabs',
          action: 'fetchAssets',
          metadata: { productId: product.id, error: err },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      // Continue to try cache on error
    }

    // If API failed or returned empty, try to use localStorage cache
    try {
      const cachedAssetsJSON = localStorage.getItem(`product_assets_${product.id}`);
      if (cachedAssetsJSON) {
        const cachedAssets = JSON.parse(cachedAssetsJSON);
        if (Array.isArray(cachedAssets) && cachedAssets.length > 0) {
          setAssets(cachedAssets);
          setLoadingAssets(false);
          return;
        }
      }
    } catch (err) {
      logError(
        'Failed to read cached assets',
        {
          component: 'ProductDetailTabs',
          action: 'fetchAssets',
          metadata: { productId: product.id, error: err },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
    }

    // If we got here, both API and cache failed - set empty assets array
    setAssets([]);
    setLoadingAssets(false);
  };

  // Add local implementation to avoid dependency issues
  const isImageAsset = (asset?: ProductAsset): boolean => {
    if (!asset) return false;

    // Check content_type or type
    const type = asset.content_type || asset.type || '';
    if (type && type.toLowerCase().startsWith('image/')) {
      return true;
    }

    // Check URL extension
    const url = asset.url || '';
    if (url && typeof url === 'string') {
      const extension = url.split('.').pop()?.toLowerCase() || '';
      return [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'svg',
        'webp',
        'bmp',
        'tiff',
        'ico',
        'heic',
        'avif',
      ].includes(extension);
    }

    return false;
  };

  // Add an effect to handle assets changes and notify the parent component
  useEffect(() => {
    // Only proceed if we have at least one asset and a product ID
    if (assets.length > 0 && product.id) {
      // Find the primary asset
      const primaryAsset = assets.find(asset => asset.is_primary);

      // If we have a primary asset and it's an image, update the parent component
      if (primaryAsset && isImageAsset(primaryAsset)) {
        // Notify parent component about updated product with type casting to avoid TS errors
        onProductUpdate({
          ...product,
          // Use type assertion to handle the primary_asset property
          primary_asset: primaryAsset.id,
        } as unknown as Product);
      }
    }
  }, [assets, product.id, isImageAsset, onProductUpdate]);

  // Add a useEffect to fetch completeness data (after the existing useEffect hooks)
  useEffect(() => {
    if (product.id) {
      const fetchProductCompleteness = async () => {
        setIsLoadingCompleteness(true);
        try {
          // Ensure we have a valid organization ID
          let organizationId: number;

          if (typeof user?.organization_id === 'string') {
            organizationId = parseInt(user.organization_id, 10);
          } else if (typeof user?.organization_id === 'number') {
            organizationId = user.organization_id;
          } else {
            logError(
              'Invalid organization ID for product completeness',
              {
                component: 'ProductDetailTabs',
                action: 'fetchProductCompleteness',
                metadata: {
                  productId: product.id,
                  organizationId: user?.organization_id,
                },
              },
              {
                showToast: true,
                userFacing: true,
              }
            );
            setIsLoadingCompleteness(false);
            return;
          }

          // Use the dashboardService to fetch product completeness with organization_id and product_id
          const completenessData = await getIncompleteProducts({
            organization_id: organizationId,
            product_id: product.id,
          });

          // If data array is returned (expected), take the first element
          if (Array.isArray(completenessData) && completenessData.length > 0) {
            setProductCompletenessDetails(completenessData[0]);
          } else if (completenessData.length === 0) {
            // Handle case where no data is returned - already converted to proper error handling above
            toast.error(getTabsNotification('completenessNotFound'));
          }
        } catch (error) {
          logError(
            'Failed to fetch product completeness',
            {
              component: 'ProductDetailTabs',
              action: 'fetchProductCompleteness',
              metadata: {
                productId: product.id,
                organizationId:
                  typeof user?.organization_id === 'string'
                    ? parseInt(user.organization_id, 10)
                    : user?.organization_id,
                isAxiosError: axios.isAxiosError(error),
                statusCode: axios.isAxiosError(error) ? error.response?.status : undefined,
              },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
        } finally {
          setIsLoadingCompleteness(false);
        }
      };

      fetchProductCompleteness();
    }
  }, [product.id, user?.organization_id]);

  // Add a new state for the field status modal near other state declarations
  const [isFieldStatusModalOpen, setIsFieldStatusModalOpen] = useState(false);

  // Add an initial collapsed state of false - keep content expanded by default
  const [isDataCompletenessCollapsed, setIsDataCompletenessCollapsed] = useState(false);

  const [units] = useState(['kg', 'g', 'lb', 'oz', 'cm', 'mm', 'm', 'in', 'ft', 'l', 'ml']);

  const [currencies, setCurrencies] = useState<
    { iso_code: string; symbol: string; name: string }[]
  >([]);

  useEffect(() => {
    axiosInstance
      .get(API_CURRENCIES)
      .then(res => setCurrencies(res.data))
      .catch(() => setCurrencies([]));
    if (typeof product.id === 'number') {
      axiosInstance
        .get(API_ENDPOINTS.products.assets(product.id))
        .then(res => setAssets(res.data))
        .catch(() => setAssets([]));
    } else {
      axiosInstance
        .get(`/api/products/${product.id}/assets/`)
        .then(res => setAssets(res.data))
        .catch(() => setAssets([]));
    }
  }, [product.id]);

  // Use consistent S3-aware URL handling for all asset URLs
  const getAssetUrlSafe = (asset: ProductAsset): string | null => {
    if (!asset) return null;

    // Always use getImageUrlFromAsset to handle S3 pre-signed URLs correctly
    // This ensures consistent behavior with other image components
    return getImageUrlFromAsset(asset, 'medium') || null;
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
      <TabsList className="w-full">
        <TabsTrigger value="overview">{getTabsLabels.labels.overview}</TabsTrigger>
        <TabsTrigger value="attributes">{getTabsLabels.labels.attributes}</TabsTrigger>
        <TabsTrigger value="assets">{getTabsLabels.labels.assets}</TabsTrigger>
        <TabsTrigger value="history">{getTabsLabels.labels.history}</TabsTrigger>
        <PermissionGuard permission="product.change">
          <TabsTrigger value="price">{getTabsLabels.labels.price}</TabsTrigger>
        </PermissionGuard>
        <TabsTrigger value="comments">{getTabsLabels.labels.comments}</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-6">
        {/* Product Description Component */}
        <ProductDetailDescription product={product} onProductUpdate={onProductUpdate} />

        {/* Data Completeness Card */}
        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>{getTabsLabels.dataCompleteness.title}</CardTitle>
              <CardDescription>{getTabsLabels.dataCompleteness.description}</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="size-8 p-0"
              onClick={() => setIsDataCompletenessCollapsed(prev => !prev)}
              aria-label={
                isDataCompletenessCollapsed
                  ? getTabsLabels.dataCompleteness.ariaLabels.expandCollapse.expand
                  : getTabsLabels.dataCompleteness.ariaLabels.expandCollapse.collapse
              }
              type="button"
            >
              {isDataCompletenessCollapsed ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronUp className="size-4" />
              )}
            </Button>
          </CardHeader>

          {/* Always show the overall completeness percentage, even when collapsed */}
          <CardContent>
            {isLoadingCompleteness && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-6 w-1/6" />
                </div>
                <Skeleton className="h-8 w-full" />
              </div>
            )}

            {!isLoadingCompleteness && !productCompletenessDetails && (
              <Alert variant="default" className="mt-4">
                <AlertTriangle className="size-4" />
                <AlertDescription>
                  {getTabsLabels.dataCompleteness.states.unavailable}
                </AlertDescription>
              </Alert>
            )}

            {!isLoadingCompleteness && productCompletenessDetails && (
              <>
                {/* Always show progress bar */}
                <div className="mb-6">
                  <div className="mb-1 flex items-center justify-between">
                    <CardDescription className="text-sm font-medium">
                      {getTabsLabels.dataCompleteness.overallCompleteness}
                    </CardDescription>
                    <span className="text-xl font-bold text-primary">
                      {Math.round(calculatedCompletenessPercentage)}%
                    </span>
                  </div>
                  <Progress
                    value={calculatedCompletenessPercentage}
                    aria-label={getTabsLabels.dataCompleteness.ariaLabels.completenessProgress.replace(
                      '{{percentage}}',
                      calculatedCompletenessPercentage.toString()
                    )}
                    className="h-3 w-full"
                  />
                </div>

                {/* Conditional content that can be collapsed */}
                {!isDataCompletenessCollapsed && (
                  <div className="duration-300 animate-in fade-in-0">
                    {/* Missing Information Section */}
                    {filteredMissingFields.length > 0 && (
                      <div className="mb-6 space-y-4">
                        <h3 className="mb-1 flex items-center gap-2 text-lg font-semibold">
                          <AlertTriangle className="size-4 text-amber-500" />
                          {getTabsLabels.dataCompleteness.missingInformation}
                        </h3>

                        {/* Critical fields */}
                        {categorizedMissingFields.critical.length > 0 && (
                          <div className="rounded-md border border-red-300 bg-red-50 p-3">
                            <h4
                              className="mb-2 flex items-center gap-1 font-medium text-red-700"
                              aria-label={getTabsLabels.dataCompleteness.ariaLabels.criticalGaps}
                            >
                              <AlertCircle className="size-4" />
                              {getTabsLabels.dataCompleteness.categories.critical}
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {categorizedMissingFields.critical.map((item, index) => (
                                <Badge
                                  key={`critical-${index}`}
                                  variant="outline"
                                  className="border-red-300 bg-red-50 text-red-700"
                                >
                                  {item.field}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recommended fields */}
                        {categorizedMissingFields.recommended.length > 0 && (
                          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                            <h4 className="mb-2 font-medium text-amber-700">
                              {getTabsLabels.dataCompleteness.categories.recommended}
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {categorizedMissingFields.recommended.map((item, index) => (
                                <Badge
                                  key={`recommended-${index}`}
                                  variant="outline"
                                  className="border-amber-200 bg-amber-50 text-amber-700"
                                >
                                  {item.field}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Optional fields */}
                        {categorizedMissingFields.optional.length > 0 && (
                          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                            <h4 className="mb-2 font-medium text-slate-700">
                              {getTabsLabels.dataCompleteness.categories.optional}
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {categorizedMissingFields.optional.slice(0, 10).map((item, index) => (
                                <Badge
                                  key={`optional-${index}`}
                                  variant="outline"
                                  className="border-slate-200 bg-slate-50 text-slate-700"
                                >
                                  {item.field}
                                </Badge>
                              ))}
                            </div>
                            {categorizedMissingFields.optional.length > 10 && (
                              <div className="mt-2 text-xs text-slate-500">
                                {getTabsLabels.dataCompleteness.states.moreDots.replace(
                                  '{{count}}',
                                  (categorizedMissingFields.optional.length - 10).toString()
                                )}
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="h-auto p-0 text-xs"
                                  onClick={() => setIsFieldStatusModalOpen(true)}
                                >
                                  {getTabsLabels.dataCompleteness.states.viewAll}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Field Status Breakdown Section */}
                    <h3 className="mb-3 border-b pb-2 text-lg font-semibold">
                      {getTabsLabels.dataCompleteness.fieldStatusBreakdown}
                    </h3>
                    {currentFieldCompleteness.length > 0 ? (
                      <div className="flex flex-col space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground">
                            {getTabsLabels.dataCompleteness.fieldsComplete
                              .replace(
                                '{{completed}}',
                                currentFieldCompleteness
                                  .filter(item => item.complete)
                                  .length.toString()
                              )
                              .replace('{{total}}', currentFieldCompleteness.length.toString())}
                          </p>

                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={e => {
                              e.preventDefault();
                              setIsFieldStatusModalOpen(true);
                            }}
                            className="ml-auto"
                          >
                            <List className="mr-2 size-4" />
                            {getTabsLabels.dataCompleteness.viewAllFieldStatuses.replace(
                              '{{count}}',
                              currentFieldCompleteness.length.toString()
                            )}
                          </Button>
                        </div>

                        {/* Status Summary */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="rounded-md border border-green-200 bg-green-50 p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <Check className="mr-2 size-4 text-green-600" />
                                <span className="font-medium text-green-700">
                                  {getTabsLabels.dataCompleteness.statusSummary.complete}
                                </span>
                              </div>
                              <Badge
                                variant="outline"
                                className="border-green-300 bg-green-100 text-green-700"
                              >
                                {currentFieldCompleteness.filter(item => item.complete).length}
                              </Badge>
                            </div>
                          </div>

                          <div className="rounded-md border border-red-200 bg-red-50 p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <AlertCircle className="mr-2 size-4 text-red-600" />
                                <span className="font-medium text-red-700">
                                  {getTabsLabels.dataCompleteness.statusSummary.incomplete}
                                </span>
                              </div>
                              <Badge
                                variant="outline"
                                className="border-red-300 bg-red-100 text-red-700"
                              >
                                {currentFieldCompleteness.filter(item => !item.complete).length}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {getTabsLabels.dataCompleteness.noFieldDetails}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Field Status Modal */}
            <FieldStatusModal
              open={isFieldStatusModalOpen}
              onOpenChange={setIsFieldStatusModalOpen}
              fieldCompleteness={currentFieldCompleteness}
              productId={product.id ?? 0}
            />

            {/* CompletenessDrilldown component (keep this unchanged) */}
            {showDrilldown && (
              <CompletenessDrilldown
                open={showDrilldown}
                onOpenChange={setShowDrilldown}
                percentage={calculatedCompletenessPercentage}
                fieldStatus={currentFieldCompleteness}
                productId={product.id ?? 0}
              />
            )}
          </CardContent>
        </Card>

        {/* Media Section - Reuse asset data from the API */}
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>{getTabsLabels.media.title}</CardTitle>
            <CardDescription>{getTabsLabels.media.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAssets ? (
              // Loading skeleton
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-md" />
                ))}
              </div>
            ) : (
              <>
                {/* Always render image grid if we have any assets */}
                {(() => {
                  const imageAssets = assets.filter(asset => {
                    return isImageAsset(asset);
                  });

                  if (imageAssets.length > 0) {
                    return (
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        {imageAssets
                          .sort((a, b) =>
                            a.is_primary && !b.is_primary
                              ? -1
                              : !a.is_primary && b.is_primary
                                ? 1
                                : 0
                          )
                          .slice(0, 4)
                          .map((asset, index) => {
                            const src = getAssetUrlSafe(asset)!; // non-null because we filtered already
                            return (
                              <div
                                key={`media-card-image-${asset.id}-${index}`}
                                className={cn(
                                  'relative aspect-square rounded-md overflow-hidden border border-slate-200',
                                  asset.is_primary && 'ring-2 ring-primary'
                                )}
                              >
                                <img
                                  src={src}
                                  alt={
                                    asset.name ||
                                    getTabsLabels.media.imageAlt.replace(
                                      '{{number}}',
                                      (index + 1).toString()
                                    )
                                  }
                                  className="size-full object-cover"
                                  loading="lazy"
                                  onError={e => {
                                    logWarn('Failed to load image asset', {
                                      component: 'ProductDetailTabs',
                                      action: 'loadImage',
                                      metadata: {
                                        src,
                                        assetId: asset.id,
                                        assetName: asset.name,
                                      },
                                    });
                                    (e.target as HTMLImageElement).src =
                                      `https://placehold.co/600x600?text=${getTabsLabels.media.errorPlaceholder}`;
                                  }}
                                />
                                {asset.is_primary && (
                                  <div className="absolute left-2 top-2">
                                    <Badge
                                      variant="outline"
                                      className="border-none bg-primary/70 text-xs text-white"
                                    >
                                      {getTabsLabels.media.primaryBadge}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    );
                  } else {
                    return (
                      // Empty state when no images are found
                      <div className="py-8 text-center text-muted-foreground">
                        <ImageIcon className="mx-auto mb-2 size-10 text-muted-foreground/60" />
                        <p>{getTabsLabels.media.noImagesAvailable}</p>
                        {hasEditPermission && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => setActiveTab('assets')}
                          >
                            <PlusIcon className="mr-2 size-4" />
                            {getTabsLabels.media.addImages}
                          </Button>
                        )}
                      </div>
                    );
                  }
                })()}

                {/* Show "View All" button if there are images */}
                {assets.filter(asset => isImageAsset(asset)).length > 0 && (
                  <div className="mt-4 flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => setActiveTab('assets')}>
                      {getTabsLabels.media.viewAllImages}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Related Products */}
        <Card>
          <CardHeader>
            <CardTitle>{getTabsLabels.actions.relatedProducts.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <RelatedProductsPanel
              productId={product.id ?? 0}
              onRefresh={() => {
                /* handle refresh if needed */
              }}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="attributes" className="space-y-6">
        {ENABLE_CUSTOM_ATTRIBUTES && (
          <ProductAttributesPanel
            key={`attribute-panel-${product.id}-${product.family || 'no-family'}-${selectedLocale}`}
            productId={product.id ? String(product.id) : ''}
            locale={selectedLocale || defaultLocale}
            channel={defaultChannel?.code || ''}
            familyId={
              typeof product.family === 'object' && product.family !== null
                ? product.family.id
                : undefined
            }
            enabled={activeTab === 'attributes'}
          />
        )}
      </TabsContent>

      <TabsContent value="assets">
        {(() => {
          try {
            return <AssetsTab product={product} />;
          } catch (error) {
            logError(
              'Failed to render AssetsTab',
              {
                component: 'ProductDetailTabs',
                action: 'renderAssetsTab',
                metadata: {
                  productId: product?.id,
                  productName: product?.name,
                  error: error,
                },
              },
              {
                showToast: true,
                userFacing: true,
              }
            );
            return <div>Error loading assets: {String(error)}</div>;
          }
        })()}
      </TabsContent>

      <TabsContent value="history" className="space-y-6">
        <Suspense fallback={<Skeleton className="h-48 w-full" />}>
          <ProductHistoryTab productId={product.id ?? 0} />
        </Suspense>
      </TabsContent>
      <PermissionGuard permission="product.change">
        <TabsContent value="price">
          <PriceTab
            productId={product.id ?? 0}
            prices={prices}
            isPricesLoading={isPricesLoading}
            onPricesUpdated={async () => await onProductUpdate(product)}
          />
        </TabsContent>
      </PermissionGuard>
      <TabsContent value="comments">
        <ProductCommentsTab productId={product.id ?? 0} />
      </TabsContent>
    </Tabs>
  );
};
